import { NotificationStarting } from "@faire/retailer-visitor-shared/events";
import { AppEvent } from "@faire/retailer-visitor-shared/packages/core/services/AppEvents";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import React, {
  Suspense,
  useState,
  useEffect,
  useCallback,
  type JSX,
} from "react";
import shortid from "shortid";

export interface IEventHandlerProps<E> {
  eventKey: string;
  eventData: E;
  handleNotificationFinished: (eventKey: string) => void;
}

interface IProps<E> {
  event: AppEvent<E>;
  eventFinish?: AppEvent<void>;
  eventHandlerElement: { (props: IEventHandlerProps<E>): JSX.Element };
  shouldIgnoreEvent?: boolean;
}

/**
 * Wrapper component that listens for events. See AddToCard for sample use.
 */
export const EventListener = <E,>({
  event,
  eventFinish,
  eventHandlerElement,
  shouldIgnoreEvent,
}: IProps<E>) => {
  const [hasAnyEventsOccurred, setHasAnyEventsOccurred] =
    useState<boolean>(false);
  const [eventData, setEventData] = useState<E | undefined>(undefined);
  const [eventKey, setEventKey] = useState<string | undefined>(undefined);

  const finishNotification = useCallback(() => {
    eventFinish?.publish();
    setEventKey(undefined);
    setEventData(undefined);
  }, [eventFinish]);

  const handleNotificationStarting = useEffectEvent((key: string) => {
    if (key !== eventKey) {
      finishNotification();
    }
  });

  const handleNotificationFinished = useCallback(
    (key: string) => {
      if (key === eventKey) {
        finishNotification();
      }
    },
    [eventKey, finishNotification]
  );

  const handleEvent = useCallback(
    (newEventData: E) => {
      if (shouldIgnoreEvent) {
        return;
      }
      const newEventKey = shortid.generate();
      setEventKey(newEventKey);
      setEventData(newEventData);
      setHasAnyEventsOccurred(true);
    },
    [shouldIgnoreEvent]
  );

  useEffect(() => {
    // Publish a new event key every time one is created
    if (eventKey) {
      NotificationStarting.publish(eventKey);
    }
  }, [eventKey]);

  useEffect(() => {
    event.subscribe(handleEvent);
    NotificationStarting.subscribe(handleNotificationStarting);
    return () => {
      event.unsubscribe(handleEvent);
      NotificationStarting.unsubscribe(handleNotificationStarting);
    };
  }, [handleEvent, event, handleNotificationStarting]);

  if (!eventKey || !eventData || !hasAnyEventsOccurred) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      {eventHandlerElement({
        eventKey: eventKey,
        eventData: eventData,
        handleNotificationFinished: handleNotificationFinished,
      })}
    </Suspense>
  );
};
